import _ from "lodash";
import { getEnabledElement } from "component/toolManager/enabledElements.js";

/**
 * Internal API function to draw an image to a given enabled element
 *
 * @param {EnabledElement} enabledElement  Enabled Element to redraw
 * @param {Boolean} [invalidated = false] - true if pixel data has been invalidated and cached rendering should not be used
 * @returns {void}
 * @memberof Internal
 */
function drawImage(enabledElement, invalidated = false) {
  enabledElement.needsRedraw = true;
  if (invalidated) {
    enabledElement.invalid = true;
  }
}

/**
 * Forces the image to be updated/redrawn for the specified enabled element
 * @param {HTMLElement} element An HTML Element enabled for
 * @param {Boolean} [invalidated=false] Whether or not the image pixel data has been changed, necessitating a redraw
 *
 * @returns {void}
 * @memberof Drawing
 */
function updateImage(element, invalidated = false) {
  const enabledElement = getEnabledElement(element);

  if (enabledElement.image === undefined && !enabledElement.layers.length) {
    throw new Error("updateImage: image has not been loaded yet");
  }

  drawImage(enabledElement, invalidated);
}

/**
 * Adjusts an image's scale and translation so the image is centered and all pixels
 * in the image are viewable.
 *
 * @param {HTMLElement} element  element to update
 * @returns {void}
 */
function fitToWindow(element) {
  var enabledElement = getEnabledElement(element);
  const { image } = enabledElement;
  let test = _.get(enabledElement.viewport, "rotation");
  if (test === undefined) {
    // 임시방편
    enabledElement = getEnabledElement(enabledElement);
  }

  // The new scale is the minimum of the horizontal and vertical scale values
  enabledElement.viewport.scale = getImageFitScale(
    enabledElement.canvas,
    image,
    enabledElement.viewport.rotation
  ).scaleFactor;

  enabledElement.viewport.translation.x = 0;
  enabledElement.viewport.translation.y = 0;
  updateImage(element);
}

// function getImageSize(image) {
//   return {
//     width: image.width,
//     height: image.height
//   };
// }

function getImageSize(image) {
  return {
    width: image.columns,
    height: image.rows,
  };
}

/**
 * This module is responsible for enabling an element to display images with
 *
 * @param {HTMLElement} element The DOM element enabled for
 * @param {HTMLElement} canvas The Canvas DOM element within the DOM element enabled for
 * @returns {void}
 */
function setCanvasSize(element, canvas) {
  // The device pixel ratio is 1.0 for normal displays and > 1.0
  // For high DPI displays like Retina
  /*

    This functionality is disabled due to buggy behavior on systems with mixed DPI's.  If the canvas
    is created on a display with high DPI (e.g. 2.0) and then the browser window is dragged to
    a different display with a different DPI (e.g. 1.0), the canvas is not recreated so the pageToPixel
    produces incorrect results.  I couldn't find any way to determine when the DPI changed other than
    by polling which is not very clean.  If anyone has any ideas here, please let me know, but for now
    we will disable this functionality.  We may want
    to add a mechanism to optionally enable this functionality if we can determine it is safe to do
    so (e.g. iPad or iPhone or perhaps enumerate the displays on the system.  I am choosing
    to be cautious here since I would rather not have bug reports or safety issues related to this
    scenario.

    var devicePixelRatio = window.devicePixelRatio;
    if(devicePixelRatio === undefined) {
        devicePixelRatio = 1.0;
    }
    */

  // Avoid setting the same value because it flashes the canvas with IE and Edge
  if (canvas.width !== element.clientWidth) {
    canvas.width = element.clientWidth;
    canvas.style.width = `${element.clientWidth}px`;
    //canvas.style.width = `100%`;
  }
  // Avoid setting the same value because it flashes the canvas with IE and Edge
  if (canvas.height !== element.clientHeight) {
    canvas.height = element.clientHeight;
    canvas.style.height = `${element.clientHeight}px`;
    //canvas.style.height = `100%`;
  }
}

/**
 * Checks if the image of a given enabled element fitted the window
 * before the resize
 *
 * @param {EnabledElement} enabledElement  Enabled Element
 * @param {number} oldCanvasWidth The width of the canvas before the resize
 * @param {number} oldCanvasHeight The height of the canvas before the resize
 * @return {Boolean} true if it fitted the windows, false otherwise
 */
function wasFitToWindow(enabledElement, oldCanvasWidth, oldCanvasHeight) {
  const scale = enabledElement.viewport.scale;
  const imageSize = getImageSize(
    enabledElement.image,
    enabledElement.viewport.rotation
  );
  const imageWidth = Math.round(imageSize.width * scale);
  const imageHeight = Math.round(imageSize.height * scale);
  const x = enabledElement.viewport.translation.x;
  const y = enabledElement.viewport.translation.y;

  return (
    (imageWidth === oldCanvasWidth && imageHeight <= oldCanvasHeight) ||
    (imageWidth <= oldCanvasWidth &&
      imageHeight === oldCanvasHeight &&
      x === 0 &&
      y === 0)
  );
}

/**
 * Calculates the horizontal, vertical and minimum scale factor for an image
   @param {{width, height}} windowSize The window size where the image is displayed. This can be any HTML element or structure with a width, height fields (e.g. canvas).
 * @param {any} image  image object
 * @param {Number} rotation Optional. The rotation angle of the image.
 * @return {{horizontalScale, verticalScale, scaleFactor}} The calculated horizontal, vertical and minimum scale factor
 * @memberof Internal
 */
function getImageFitScale(windowSize, image, rotation = null) {
  const imageSize = getImageSize(image, rotation);
  let rowPixelSpacing = 1;
  let columnPixelSpacing = 1;
  if (image.pixelSpacing !== null && image.pixelSpacing !== undefined) {
    rowPixelSpacing = image.pixelSpacing[0] || 1;
    columnPixelSpacing = image.pixelSpacing[1] || 1;
  }

  let verticalRatio = 1;
  let horizontalRatio = 1;

  if (rowPixelSpacing < columnPixelSpacing) {
    horizontalRatio = columnPixelSpacing / rowPixelSpacing;
  } else {
    // even if they are equal we want to calculate this ratio (the ration might be 0.5)
    verticalRatio = rowPixelSpacing / columnPixelSpacing;
  }

  const verticalScale = windowSize.height / imageSize.height / verticalRatio;
  const horizontalScale = windowSize.width / imageSize.width / horizontalRatio;

  // Fit image to window
  return {
    verticalScale,
    horizontalScale,
    scaleFactor: Math.min(horizontalScale, verticalScale),
  };
}

/**
 * Rescale the image relative to the changed size of the canvas
 *
 * @param {EnabledElement} enabledElement  Enabled Element
 * @param {number} oldCanvasWidth The width of the canvas before the resize
 * @param {number} oldCanvasHeight The height of the canvas before the resize
 * @return {void}
 */
function relativeRescale(enabledElement, oldCanvasWidth, oldCanvasHeight) {
  const scale = enabledElement.viewport.scale;
  const canvasWidth = enabledElement.canvas.width;
  const canvasHeight = enabledElement.canvas.height;
  const relWidthChange = canvasWidth / oldCanvasWidth;
  const relHeightChange = canvasHeight / oldCanvasHeight;
  const relChange = Math.sqrt(relWidthChange * relHeightChange);

  enabledElement.viewport.scale = relChange * scale;
}

/**
 * Resizes an enabled element and optionally fits the image to window
 *
 * @param {HTMLElement} element The DOM element enabled for
 * @param {Boolean} forceFitToWindow true to to force a refit, false to rescale accordingly
 * @returns {void}
 */
export default function (element, forceFitToWindow, test = false) {
  if (element.clientWidth === 0 || element.clientHeight === 0) {
    return;
  }
  let enabledElement = getEnabledElement(element);

  const oldCanvasWidth = enabledElement.canvas.width;
  const oldCanvasHeight = enabledElement.canvas.height;

  setCanvasSize(element, enabledElement.canvas);

  if (enabledElement.image === undefined || enabledElement.image === null) {
    return;
  }
  const hasScale = _.get(enabledElement, "viewport.scale");
  if (hasScale === null) {
    // 임시방편
    enabledElement = getEnabledElement(enabledElement);
  }

  if (
    test === false &&
    (forceFitToWindow ||
      wasFitToWindow(enabledElement, oldCanvasWidth, oldCanvasHeight))
  ) {
    // Fit the image to the window again if it fitted before the resize
    fitToWindow(element);
  } else {
    // Adapt the scale of a zoomed or panned image relative to the size change
    relativeRescale(enabledElement, oldCanvasWidth, oldCanvasHeight);

    updateImage(element);
  }
}

// export default function(element, forceFitToWindow) {
//   let enabledElement = getEnabledElement(element);

//   const oldCanvasWidth = enabledElement.canvas.width;
//   const oldCanvasHeight = enabledElement.canvas.height;

//   setCanvasSize(element, enabledElement.canvas);

//   if (enabledElement.image === undefined || enabledElement.image === null) {
//     return;
//   }

//   if (!_.get(enabledElement, "viewport.scale")) {
//     // 임시방편
//     enabledElement = getEnabledElement(enabledElement);
//   }

//   if (
//     forceFitToWindow ||
//     wasFitToWindow(enabledElement, oldCanvasWidth, oldCanvasHeight)
//   ) {
//     // Fit the image to the window again if it fitted before the resize
//     fitToWindow(element);
//   } else {
//     // Adapt the scale of a zoomed or panned image relative to the size change
//     relativeRescale(enabledElement, oldCanvasWidth, oldCanvasHeight);

//     updateImage(element);
//   }
// }
